import {
  ColumnFactory,
  createDataQualityCellFormatter,
  createIconCellFormatter,
  createSelectionColumn,
  createSortableHeaderFormatter,
  createTextCellFormatter,
  DataQualityAssetType,
  DataQualityCellFormatterRowDataFunctions,
  DataQualityIconHeaderCertificationFormatterFactory,
  DataQualityIconHeaderFormatterFactory,
  ExplorerStyledDisplayGrid,
  GraphQLError,
  GridSortProps,
  reportGraphQLErrors,
  SelectionProviderChildProps,
  UnsortableColumnKey,
  UsualDisplayGridProps,
} from '@tableau/data-catalog-shared';
import { SiteUrls } from '@tableau/shared-urls';
import * as React from 'react';

import { Formatters, Messages } from '../../../gen-src/Localize';
import { DatabaseTableOrderField } from '../../../gen-src/queries/globalTypes';
import {
  createQueryPreservingReplacingLinkCellFormatter,
} from '../datagrid/cellFormatters/QueryPreservingLinkCellFormatter';
import { VirtualConnectionTableTypeIcon } from '../datagrid/cellFormatters/VirtualConnectionTableTypeIcon';
import { GlobalAssetGridDC3ColumnFactoryParams } from '../sorting/makeColumnFactoryParams';
import { TablesValidSortFields } from '../sorting/sortProviders/TablesGridSortProvider';
import { VirtualConnectionTableTypeMappings } from '../table/functions/VirtualConnectionTableTypeMappings';
import { VirtualConnectionTablesDisplayGridRowData } from "../table/types/VirtualConnectionTablesDisplayGridRowData";

type VirtualConnectionTablesColumnFactory = ColumnFactory<
  GlobalAssetGridDC3ColumnFactoryParams<TablesValidSortFields>,
  VirtualConnectionTablesDisplayGridRowData,
  TablesValidSortFields
>;

const rowDataFunctions: DataQualityCellFormatterRowDataFunctions<VirtualConnectionTablesDisplayGridRowData> = {
  getAssetUrlFromRowData: (urls: SiteUrls, { id }) => urls.virtualConnectionTableUrl(id),
  getNameFromRowData: ({ name }) => name || '',
  getTypeNameFromRowData: (): DataQualityAssetType => 'VirtualConnectionTable',
  getDataQualityCertificationFromRowData: ({ dataQualityCertifications }) => dataQualityCertifications
}

const columns: ReadonlyArray<VirtualConnectionTablesColumnFactory> = [
  () => createSelectionColumn(),
  () => ({
    name: 'certification',
    dataKey: '',
    headerText: '',
    headerTooltip: Messages.Certification(),
    headerFormatter: DataQualityIconHeaderCertificationFormatterFactory,
    cellFormatter: createDataQualityCellFormatter({
      getWarningsFromRowData: ({ dataQualityWarnings }) => dataQualityWarnings,
      disableIconOnlyTabIndex: true,
      isCertification: true,
      rowDataFunctions: rowDataFunctions,
    }),
    sortable: false,
    resizable: false,
  }),
  () => ({
    name: 'warning',
    dataKey: '',
    headerText: '',
    headerTooltip: Messages.DataQualityWarningTitle(),
    headerFormatter: DataQualityIconHeaderFormatterFactory,
    cellFormatter: createDataQualityCellFormatter({
      getWarningsFromRowData: ({ dataQualityWarnings }) => dataQualityWarnings,
      disableIconOnlyTabIndex: true,
      isCertification: false,
      rowDataFunctions: rowDataFunctions,
    }),
    sortable: false,
    resizable: false,
  }),
  (sortParams) => ({
    name: 'type',
    dataKey: UnsortableColumnKey,
    headerText: Messages.GridHeaderType(),
    headerFormatter: createSortableHeaderFormatter(sortParams),
    cellFormatter: createIconCellFormatter(
      ({ isExtracted }) => <VirtualConnectionTableTypeIcon
        isExtracted={!!isExtracted}
      />,
      ({ isExtracted }) => VirtualConnectionTableTypeMappings({
        isExtracted: !!isExtracted,
      }),
    ),
    sortable: false,
    resizable: false
  }),
  (sortParams) => ({
    name: 'name',
    dataKey: DatabaseTableOrderField.NAME,
    headerText: Messages.GridHeaderName(),
    headerFormatter: createSortableHeaderFormatter(sortParams),
    cellFormatter: createQueryPreservingReplacingLinkCellFormatter(
      ({ name }) => name || '',
      (urls, { vizportalUrlId }) => urls.virtualConnectionTableLineagePath(vizportalUrlId),
    ),
    sortable: true,
  }),
  (sortParams) => ({
    name: 'workbooks',
    dataKey: DatabaseTableOrderField.DOWNSTREAM_WORKBOOKS_COUNT,
    headerText: Messages.GridHeaderWorkbooks(),
    headerFormatter: createSortableHeaderFormatter(sortParams),
    cellFormatter: createTextCellFormatter(
      ({ downstreamWorkbooksConnection }) =>
        downstreamWorkbooksConnection
          ? Formatters.formatCount(downstreamWorkbooksConnection.totalCount)
          : null
    ),
    sortable: true,
  }),
  (sortParams) => ({
    name: 'datasources',
    dataKey: DatabaseTableOrderField.DOWNSTREAM_DATASOURCES_COUNT,
    headerText: Messages.GridHeaderDatasources(),
    headerFormatter: createSortableHeaderFormatter(sortParams),
    cellFormatter: createTextCellFormatter(
      ({ downstreamDatasourcesConnection }) =>
        downstreamDatasourcesConnection
          ? Formatters.formatCount(downstreamDatasourcesConnection.totalCount)
          : null
    ),
    sortable: true,
  }),
  (sortParams) => ({
    name: 'extractStatus',
    dataKey: UnsortableColumnKey,
    cellFormatter: createTextCellFormatter(
      ({ isExtracted }) =>
        isExtracted
          ? Messages.ExtractConnection()
          : Messages.LiveConnection()
    ),
    headerText: Messages.GridHeaderConnectionStatus(),
    headerFormatter: createSortableHeaderFormatter(sortParams),
    sortable: false,
  }),
];

export type VirtualConnectionTablesDisplayGridProps =
  UsualDisplayGridProps<VirtualConnectionTablesDisplayGridRowData, GraphQLError> &
  SelectionProviderChildProps &
  GridSortProps<TablesValidSortFields>;

export function VirtualConnectionTablesDisplayGrid(props: VirtualConnectionTablesDisplayGridProps): JSX.Element {
  const cols = columns.map(columnFactory => columnFactory(props));

  return (
    <ExplorerStyledDisplayGrid
      columns={cols}
      rowStore={props.rowStore}
      selectedKeys={props.selectedKeys}
      updateSelectionForKey={props.updateSelectionForKey}
      rowCount={props.rowCount}
      renderErrors={reportGraphQLErrors}
      orderBy={props.orderBy}
      updateSortOrder={props.updateSortOrder}
    />
  );
}
